import {I18n} from '@shopify/react-i18n';
import {sentenceCase} from 'change-case';
import type {
  MachineTranslation,
  TranslatableEntry,
} from 'features/Translations/TranslatableResource/types';
import {
  isGroupedKey,
  isTranslatableResourceWithHandleTranslation,
} from 'features/Translations/TranslatableResource/utils';
import {ExecutionResult} from 'graphql';
import {TranslatableResourceType} from 'types';
import {Market, ShopLocale} from 'utilities/use-shop-locales-and-markets';
import {LocalizableContent} from 'types/graphql/default-types';
import {parseGidType} from '@web-utilities/other';
import groupBy from 'lodash/groupBy';
import {Dictionary} from 'lodash';

import {TranslatableField} from '../components/types';
import {CreateMarketLocalizationsMutationData} from '../graphql/CreateMarketLocalizationsMutation.graphql';
import {CreateTranslationsMutationData} from '../graphql/CreateTranslationsMutation.graphql';
import {DeleteMarketLocalizationsMutationData} from '../graphql/DeleteMarketLocalizationsMutation.graphql';
import {DeleteTranslationsMutationData} from '../graphql/DeleteTranslationsMutation.graphql';
import {ResourcePreviewQueryQueryData} from '../hooks/useFilesPreview/graphql/ResourcePreviewQuery.graphql';
import {FilesPreview} from '../hooks';
import {cookieBannerGrouping} from '../../../utils/cookieBannerGrouping';

type IdenticalFieldComparatorInput = Pick<
  TranslatableField,
  'fieldKey' | 'reference' | 'value'
>;

export const PLACEHOLDER_YOUTUBE_URL =
  'https://www.youtube.com/watch?v=_9VUPq3SxOc';

export function isIdenticalField(
  field1: IdenticalFieldComparatorInput,
  field2: IdenticalFieldComparatorInput,
) {
  if (!field1.reference || !field2.reference) return false;

  return (
    field1.fieldKey === field2.fieldKey &&
    field1.reference === field2.reference &&
    field1.value === field2.value
  );
}

export function buildFields({
  translatableEntries,
  filteredTranslatableEntries,
  filesPreview,
  resourceIdsForPreview,
  filenamesForPreview,
  machineTranslations = [],
  currentMarket,
  currentLocale,
  primaryLocale,
  i18n,
}: {
  translatableEntries: TranslatableEntry[];
  filteredTranslatableEntries: TranslatableEntry[];
  filesPreview: FilesPreview;
  resourceIdsForPreview: string[];
  filenamesForPreview: string[];
  machineTranslations?: MachineTranslation[];
  currentMarket?: Market;
  currentLocale?: ShopLocale;
  primaryLocale?: ShopLocale;
  i18n: I18n;
}) {
  const fieldValuesThatAreFiles = [
    ...resourceIdsForPreview,
    ...filenamesForPreview,
  ];

  return translatableEntries
    .filter(({gidKey: source}) =>
      filteredTranslatableEntries.find(({gidKey: target}) => source === target),
    )
    .reduce<TranslatableField[]>((acc, entry) => {
      const machineTranslation = machineTranslations?.find(
        ({key}) => key === entry.key,
      );
      const defaultContentForMarket =
        (currentMarket && currentLocale?.locale === primaryLocale?.locale) ||
        entry.isOnlyMarketLocalizable ||
        entry.isContextualizedThemeContent
          ? entry.defaultContent?.value
          : entry.translatedContent?.value;
      const reference = currentMarket
        ? defaultContentForMarket
        : entry.defaultContent?.value;
      const value = currentMarket
        ? entry.translatedMarketContent?.value
        : entry.translatedContent?.value;

      const isFile =
        entry.metafieldDetails?.type.toUpperCase() ===
          LocalizableContent.FileReference ||
        (reference && fieldValuesThatAreFiles.includes(reference));
      const isShopImageReference =
        isFile &&
        reference?.startsWith('shopify://shop_images/') &&
        filenamesForPreview.includes(reference);
      const isShopVideoReference =
        isFile &&
        reference?.startsWith('shopify://files/videos/') &&
        filenamesForPreview.includes(reference);

      const originalFiles = (() => {
        if (!isFile) return undefined;

        if (isShopImageReference || isShopVideoReference) {
          const prefix = isShopImageReference
            ? 'shopify://shop_images/'
            : 'shopify://files/videos/';
          return {
            reference: filesPreview.files.find(
              ({displayName}) => `${prefix}${displayName}` === reference,
            ),
            value: filesPreview.files.find(
              ({displayName}) => `${prefix}${displayName}` === value,
            ),
          };
        } else {
          return {
            reference: filesPreview.nodes.find(({id}) => id === reference),
            value: filesPreview.nodes.find(({id}) => id === value),
          };
        }
      })();

      const fileTypesAllowed = (() => {
        if (!isFile) return [];

        if (isShopImageReference) {
          return ['Image'];
        } else if (isShopVideoReference) {
          return ['Video'];
        }

        if (
          entry.metafieldDetails?.validations &&
          entry.metafieldDetails?.validations[0]
        ) {
          try {
            return JSON.parse(entry.metafieldDetails?.validations[0]);
          } catch (err) {
            return ['All'];
          }
        }

        return ['All'];
      })();

      const isLocalizableContent = (type: string): type is LocalizableContent =>
        Object.values(LocalizableContent).includes(type as LocalizableContent);

      const translatableFieldFromEntry: TranslatableField = {
        fieldKey: entry.key,
        gidKey: entry.gidKey,
        resourceId: entry.resourceId,
        translatableResourceType: entry.translatableResourceType,
        reference: reference || '',
        value: value || '',
        dirty: false,
        label: buildLabel(entry, i18n),
        type:
          entry.defaultContent.type &&
          isLocalizableContent(entry.defaultContent.type)
            ? entry.defaultContent.type
            : undefined,
        error: '',
        hidden:
          entry.defaultContent.type === 'URL' &&
          entry.defaultContent.value === PLACEHOLDER_YOUTUBE_URL,
        display: entry.display,
        metafieldDetails: entry.metafieldDetails,
        machineTranslation,
        isOnlyMarketLocalizable: entry.isOnlyMarketLocalizable,
        isContextualizedThemeContent:
          entry.isContextualizedThemeContent ?? false,
        fileReference: isFile
          ? {
              fieldType:
                isShopImageReference || isShopVideoReference
                  ? 'filename'
                  : 'gid',
              fileTypesAllowed,
              reference: filesPreview.loading
                ? undefined
                : buildFileReference(originalFiles?.reference),
              value: filesPreview.loading
                ? undefined
                : buildFileReference(originalFiles?.value),
            }
          : undefined,
        status: {
          machineTranslated: Boolean(
            entry.translatedContent?.machineTranslated,
          ),
          updatedAt: entry.translatedContent?.updatedAt,
        },
      };

      if (
        isGroupedKey(
          entry.translatableResourceType,
          entry.key,
          entry.metafieldDetails?.ownerType,
        )
      ) {
        const computedGroupKey =
          entry.metafieldDetails &&
          entry.metafieldDetails.ownerType === 'PRODUCTVARIANT'
            ? `${entry.translatableResourceType}-${entry.metafieldDetails.ownerType}-${entry.metafieldDetails.namespace}-${entry.metafieldDetails.key}`
            : `${entry.translatableResourceType}-${entry.display.position}`;
        const targetGroup = acc.find(
          (value) => value.groupKey === computedGroupKey,
        );
        if (targetGroup) {
          translatableFieldFromEntry.hidden = acc.some((value) =>
            isIdenticalField(value, translatableFieldFromEntry),
          );
          translatableFieldFromEntry.groupKey = targetGroup.groupKey;
        } else {
          translatableFieldFromEntry.groupKey = computedGroupKey;
        }
      }

      return [...acc, translatableFieldFromEntry];
    }, []);
}

export function buildLabel(field: TranslatableEntry, i18n: I18n) {
  if (
    isTranslatableResourceWithHandleTranslation(
      field.translatableResourceType,
      field.key,
    )
  ) {
    return i18n.translate('urlHandle', {
      scope: ['FieldsLabels'],
    });
  }

  if (field.display.title) {
    return field.display.title;
  }

  const translationKey = `${field.translatableResourceType.toLowerCase()}_${
    field.key
  }`;

  return i18n.translationKeyExists(`FieldsLabels.${translationKey}`)
    ? i18n.translate(translationKey, {
        scope: ['FieldsLabels'],
      })
    : sentenceCase(field.key.split('.').pop() ?? field.key);
}

export function buildMarketLocalizationValue(
  value: string,
  translatableResourceType: TranslatableResourceType,
  market: Market,
  metafieldType?: string,
) {
  if (
    [
      TranslatableResourceType.Metafield,
      TranslatableResourceType.Metaobject,
    ].includes(translatableResourceType) &&
    metafieldType === 'money'
  ) {
    return `{"amount":"${value}","currency_code":"${market?.currencySettings?.baseCurrency.currencyCode}"}`;
  }

  return value;
}

export function buildFileReference(
  file?:
    | ResourcePreviewQueryQueryData.NodesGenericFile
    | ResourcePreviewQueryQueryData.NodesMediaImage
    | ResourcePreviewQueryQueryData.NodesVideo
    | ResourcePreviewQueryQueryData.NodesOther
    | ResourcePreviewQueryQueryData.FilesEdgesNodeGenericFile
    | ResourcePreviewQueryQueryData.FilesEdgesNodeMediaImage
    | ResourcePreviewQueryQueryData.FilesEdgesNodeVideo
    | ResourcePreviewQueryQueryData.FilesEdgesNodeOther,
) {
  if (
    !file ||
    !['MediaImage', 'GenericFile', 'Video'].includes(file.__typename)
  ) {
    return null;
  }

  const validFile = file as
    | ResourcePreviewQueryQueryData.NodesGenericFile
    | ResourcePreviewQueryQueryData.NodesMediaImage
    | ResourcePreviewQueryQueryData.NodesVideo
    | ResourcePreviewQueryQueryData.FilesEdgesNodeGenericFile
    | ResourcePreviewQueryQueryData.FilesEdgesNodeVideo
    | ResourcePreviewQueryQueryData.FilesEdgesNodeMediaImage;

  return {
    id: validFile.id || '',
    alt: validFile?.alt || '',
    displayName: validFile?.displayName || '',
    previewUrl: validFile?.preview?.image?.url || '',
  };
}

export function splitResourcesBySpecificTypes(
  fields: TranslatableField[],
  market?: Market,
) {
  return fields.reduce<{
    mainResource: TranslatableField[];
    productOptions: TranslatableField[];
    metafields: TranslatableField[];
    filterGroups: TranslatableField[];
  }>(
    (acc, field) => {
      if (
        isTranslatableResourceWithHandleTranslation(
          field.translatableResourceType,
          field.fieldKey,
        ) &&
        market
      ) {
        return acc;
      }

      if (
        field.translatableResourceType ===
          TranslatableResourceType.ProductOption ||
        field.translatableResourceType ===
          TranslatableResourceType.ProductOptionValue
      ) {
        acc.productOptions.push(field);
        return acc;
      }

      if (
        field.translatableResourceType === TranslatableResourceType.Metafield
      ) {
        acc.metafields.push(field);
        return acc;
      }

      if (
        field.translatableResourceType ===
        TranslatableResourceType.OnlineStoreFilterSettingGroup
      ) {
        acc.filterGroups.push(field);
        return acc;
      }

      acc.mainResource.push(field);
      return acc;
    },
    {
      mainResource: [],
      productOptions: [],
      metafields: [],
      filterGroups: [],
    },
  );
}

export function groupThemeAssets(fields: TranslatableField[]) {
  return fields.reduce<{[key: string]: TranslatableField[]}>((acc, field) => {
    // Top-level of the key
    const category = field.fieldKey.split('.').shift() ?? '';
    const keyArray = field.fieldKey
      // Strip the key of the top level
      .substring(field.fieldKey.indexOf('.') + 1)
      .split('.');
    // Removing the leaf of the key since that is the field's title and is not part of the group name
    keyArray.pop();
    const assetKey = keyArray.join('_');

    if (assetKey === '') {
      // Use top-level of key as the heading when it has no groupings
      (acc[category] = acc[category] || []).push(field);
    } else {
      (acc[assetKey] = acc[assetKey] || []).push(field);
    }
    return acc;
  }, {});
}

export function groupCookieBannerFields(
  fields: TranslatableField[],
): Dictionary<TranslatableField[]> {
  if (fields[0]?.resourceId?.startsWith('gid://shopify/CookieBanner')) {
    // The data in the component corresponds to actual cookie banner data. Group away:
    return groupBy(
      fields,
      (field) =>
        cookieBannerGrouping[
          field.fieldKey as keyof typeof cookieBannerGrouping
        ].subsection,
    );
  } else {
    // The data in the component is stale and corresponds to a different type.
    // Don't attempt to group as the data looks different and will crash, just
    // give up and wait for the component to remount:
    return {};
  }
}

interface TranslationMutationVariables<T> {
  [key: string]: {
    variables: T;
  };
}
interface TranslatableResourceFormMutations {
  createTranslationsMutations: {
    variables: CreateTranslationsMutationData.Variables;
  }[];
  deleteTranslationsMutations: {
    variables: DeleteTranslationsMutationData.Variables;
  }[];
  createMarketLocalizationsMutations: {
    variables: CreateMarketLocalizationsMutationData.Variables;
  }[];
  deleteMarketLocalizationsMutations: {
    variables: DeleteMarketLocalizationsMutationData.Variables;
  }[];
}
export function buildMutationsFromDirtyFields(
  filteredTranslatableEntries: TranslatableEntry[],
  dirtyFields: TranslatableField[],
  locale: string,
  currentMarket?: Market,
): TranslatableResourceFormMutations {
  const mutations: TranslatableResourceFormMutations = {
    createTranslationsMutations: [],
    deleteTranslationsMutations: [],
    createMarketLocalizationsMutations: [],
    deleteMarketLocalizationsMutations: [],
  };
  const dirtyGids = dirtyFields.map((input) => input.gidKey);
  const translatableFieldsToUpdate = [...dirtyFields].filter(
    ({value, isOnlyMarketLocalizable}) => value && !isOnlyMarketLocalizable,
  );
  const translatableFieldsToDelete = [...dirtyFields].filter(
    ({value, isOnlyMarketLocalizable}) => !value && !isOnlyMarketLocalizable,
  );
  const marketLocalizableFieldsToUpdate = [...dirtyFields].filter(
    ({value, isOnlyMarketLocalizable}) => value && isOnlyMarketLocalizable,
  );
  const marketLocalizableFieldsToDelete = [...dirtyFields].filter(
    ({value, isOnlyMarketLocalizable}) => !value && isOnlyMarketLocalizable,
  );

  const matchingTranslatableEntries = filteredTranslatableEntries
    .filter((entry) => dirtyGids.includes(entry.gidKey))
    .reduce<{
      [key: string]: TranslatableEntry;
    }>(
      (acc, entry) => ({
        ...acc,
        [entry.gidKey]: entry,
      }),
      {},
    );

  mutations.createTranslationsMutations = Object.values(
    translatableFieldsToUpdate.reduce<
      TranslationMutationVariables<CreateTranslationsMutationData.Variables>
    >((acc, {gidKey, value}) => {
      const {
        resourceId,
        key,
        defaultContent: {digest},
      } = matchingTranslatableEntries[gidKey];

      return {
        ...acc,
        [resourceId]: {
          variables: {
            id: resourceId,
            translations: [
              ...(acc?.[resourceId]?.variables.translations || []),
              {
                locale,
                key,
                value,
                translatableContentDigest: digest || '',
                marketId: currentMarket?.id,
              },
            ],
          },
        },
      };
    }, {}),
  );

  mutations.deleteTranslationsMutations = Object.values(
    translatableFieldsToDelete.reduce<
      TranslationMutationVariables<DeleteTranslationsMutationData.Variables>
    >((acc, {gidKey}) => {
      const {resourceId, key} = matchingTranslatableEntries[gidKey];

      return {
        ...acc,
        [resourceId]: {
          variables: {
            id: resourceId,
            locales: [locale],
            marketIds: currentMarket?.id ? [currentMarket.id] : undefined,
            translationKeys: [
              ...(acc?.[resourceId]?.variables.translationKeys || []),
              ...[key],
            ],
          },
        },
      };
    }, {}),
  );

  mutations.createMarketLocalizationsMutations = Object.values(
    marketLocalizableFieldsToUpdate.reduce<
      TranslationMutationVariables<CreateMarketLocalizationsMutationData.Variables>
    >((acc, {gidKey, value, translatableResourceType}) => {
      const {
        resourceId,
        key,
        defaultContent: {digest},
        metafieldDetails,
      } = matchingTranslatableEntries[gidKey];

      return {
        ...acc,
        [resourceId]: {
          variables: {
            id: resourceId,
            marketLocalizations: [
              ...(acc?.[resourceId]?.variables.marketLocalizations || []),
              {
                marketId: currentMarket!.id,
                key,
                value: buildMarketLocalizationValue(
                  value,
                  translatableResourceType,
                  currentMarket!,
                  metafieldDetails?.type,
                ),
                marketLocalizableContentDigest: digest || '',
              },
            ],
          },
        },
      };
    }, {}),
  );

  mutations.deleteMarketLocalizationsMutations = Object.values(
    marketLocalizableFieldsToDelete.reduce<
      TranslationMutationVariables<DeleteMarketLocalizationsMutationData.Variables>
    >((acc, {gidKey}) => {
      const {resourceId, key} = matchingTranslatableEntries[gidKey];

      return {
        ...acc,
        [resourceId]: {
          variables: {
            id: resourceId,
            marketIds: [currentMarket!.id],
            marketLocalizationKeys: [
              ...(acc?.[resourceId]?.variables.marketLocalizationKeys || []),
              ...[key],
            ],
          },
        },
      };
    }, {}),
  );

  return mutations;
}

interface MutationsResults {
  successfulMutationsValues: {[key: string]: string};
  failedMutationsFields: {[key: string]: TranslatableField};
}
export function buildMutationsResults(
  results: PromiseSettledResult<
    | ExecutionResult<CreateTranslationsMutationData>
    | ExecutionResult<DeleteTranslationsMutationData>
    | ExecutionResult<CreateMarketLocalizationsMutationData>
    | ExecutionResult<DeleteMarketLocalizationsMutationData>
  >[],
  createTranslationsMutations: TranslatableResourceFormMutations['createTranslationsMutations'],
  createMarketLocalizationsMutations: TranslatableResourceFormMutations['createMarketLocalizationsMutations'],
  newFields: TranslatableField[],
): MutationsResults {
  const keys: MutationsResults = {
    successfulMutationsValues: {},
    failedMutationsFields: {},
  };

  results.forEach((result, mutationIndex) => {
    if (result.status === 'rejected') {
      throw new Error(result.reason);
    }

    // Add errors from translationsRegister
    if (
      result.status === 'fulfilled' &&
      result.value?.data &&
      'translationsRegister' in result.value.data &&
      result.value?.data?.translationsRegister?.userErrors?.length
    ) {
      result.value.data.translationsRegister.userErrors.forEach((error) => {
        const indexOfError = error.field?.[1]
          ? Number(error.field[1])
          : undefined;
        if (indexOfError === undefined) return;

        const resourceId =
          createTranslationsMutations[mutationIndex].variables.id;
        const fieldKey =
          createTranslationsMutations[mutationIndex].variables.translations[
            indexOfError
          ].key;
        const keyForError = `${resourceId}/${fieldKey}`;
        const errorField = newFields.find(
          (field) => field.gidKey === keyForError,
        );

        if (errorField === undefined) return;
        errorField.error = formatErrorMessage(error.message)[0];
        keys.failedMutationsFields[keyForError] = errorField;
      });
    }

    // Add errors from marketLocalizationsRegister
    if (
      result.status === 'fulfilled' &&
      result.value?.data &&
      'marketLocalizationsRegister' in result.value.data &&
      result.value?.data?.marketLocalizationsRegister?.userErrors?.length
    ) {
      result.value.data.marketLocalizationsRegister.userErrors.forEach(
        (error) => {
          const indexOfError = error.field?.[1]
            ? Number(error.field[1])
            : undefined;
          if (indexOfError === undefined) return;

          const resourceId =
            createMarketLocalizationsMutations[mutationIndex].variables.id;
          const fieldKey =
            createMarketLocalizationsMutations[mutationIndex].variables
              .marketLocalizations[indexOfError].key;
          const keyForError = `${resourceId}/${fieldKey}`;
          const errorField = newFields.find(
            (field) => field.gidKey === keyForError,
          );

          if (errorField === undefined) return;
          errorField.error = formatErrorMessage(error.message)[0];
          keys.failedMutationsFields[keyForError] = errorField;
        },
      );
    }

    // Add successful mutations from translationsRegister
    if (
      result.status === 'fulfilled' &&
      result.value?.data &&
      'translationsRegister' in result.value.data &&
      result.value.data.translationsRegister.translations
    ) {
      result.value.data.translationsRegister.translations.forEach(
        (translation) => {
          const resourceId =
            createTranslationsMutations[mutationIndex].variables.id;
          const gidKey = `${resourceId}/${translation.key}`;
          keys.successfulMutationsValues[gidKey] = translation.value!;
        },
      );
    }

    // Add successful mutations from marketLocalizationsRegister
    if (
      result.status === 'fulfilled' &&
      result.value?.data &&
      'marketLocalizationsRegister' in result.value.data &&
      result.value.data.marketLocalizationsRegister.marketLocalizations
    ) {
      result.value.data.marketLocalizationsRegister.marketLocalizations.forEach(
        (localization) => {
          const resourceId =
            createMarketLocalizationsMutations[mutationIndex].variables.id;
          const resourceType = parseGidType(resourceId).toUpperCase();
          const gidKey = `${resourceId}/${localization.key}`;

          if (
            (resourceType === TranslatableResourceType.Metafield ||
              resourceType === TranslatableResourceType.Metaobject) &&
            newFields.find((field) => field.gidKey === gidKey)?.metafieldDetails
              ?.type === 'money'
          ) {
            const parsedValue = JSON.parse(localization.value!);
            keys.successfulMutationsValues[gidKey] = parsedValue.amount;
          } else {
            keys.successfulMutationsValues[gidKey] = localization.value!;
          }
        },
      );
    }
  });

  return keys;
}

export function formatErrorMessage(error: string) {
  const matches = Array.from(error.matchAll(/"(.*?)"/g), (match) => match[1]);
  if (!matches.length) return [error];
  return matches;
}
